import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen, Plus, FileText, Trash2, Loader2,
  FolderClosed, FolderOpen, Save, ArrowLeft,
  MoreHorizontal, Pencil, FolderInput,
} from 'lucide-react';

import {
  useNotes, useNotebooks,
  useCreateNote, useUpdateNote, useDeleteNote, useMoveNote,
  useCreateNotebook, useRenameNotebook, useDeleteNotebook,
  type Note, type Notebook,
} from '../../hooks/student/useNotes';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ─── Move-To Dropdown ───────────────────────────────────────────────────────

function MoveToDropdown({
  notebooks,
  currentNotebookId,
  onMove,
  onClose,
}: {
  notebooks: Notebook[];
  currentNotebookId: string | null;
  onMove: (notebookId: string | null) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-30 bg-[#2A2626] border border-[#504A4A] rounded-lg shadow-xl py-1 min-w-[180px]">
      <p className="px-3 py-1.5 text-[10px] text-[#A9A3A3] uppercase tracking-wider font-semibold">Move to</p>
      <button
        onClick={() => { onMove(null); onClose(); }}
        disabled={currentNotebookId === null}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#EBFFEE] hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <FileText className="w-3 h-3" />
        No Notebook
      </button>
      {notebooks.map((nb) => (
        <button
          key={nb.id}
          onClick={() => { onMove(nb.id); onClose(); }}
          disabled={currentNotebookId === nb.id}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#EBFFEE] hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <FolderClosed className="w-3 h-3" />
          <span className="truncate">{nb.title}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Notebook Sidebar ───────────────────────────────────────────────────────

function NotebookSidebar({
  notebooks,
  activeNotebookId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  isCreating,
  noteCountByNotebook,
  totalNoteCount,
  dragOverNotebookId,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  notebooks: Notebook[];
  activeNotebookId: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (title: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  isCreating: boolean;
  noteCountByNotebook: Record<string, number>;
  totalNoteCount: number;
  dragOverNotebookId: string | null;
  onDragOver: (e: React.DragEvent, notebookId: string | null) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, notebookId: string | null) => void;
}) {
  const [showNewInput, setShowNewInput] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleCreate = () => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setNewTitle('');
    setShowNewInput(false);
  };

  const handleRenameSubmit = (id: string) => {
    const trimmed = renameValue.trim();
    if (trimmed) onRename(id, trimmed);
    setRenamingId(null);
  };

  const startRename = (nb: Notebook) => {
    setRenamingId(nb.id);
    setRenameValue(nb.title);
    setMenuOpen(null);
  };

  return (
    <div className="w-56 min-w-[224px] border-r border-[#504A4A] flex flex-col bg-[#1E1B1B]">
      {/* Header */}
      <div className="px-3 h-[52px] shrink-0 border-b border-[#504A4A] flex items-center justify-between">
        <h3 className="text-xs font-semibold text-[#A9A3A3] uppercase tracking-wider">Notebooks</h3>
        <button
          onClick={() => setShowNewInput(true)}
          className="w-7 h-7 rounded-lg bg-[#9B1421] hover:bg-[#7A0F1A] flex items-center justify-center text-white transition-colors"
          title="New notebook"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* New notebook input */}
      {showNewInput && (
        <div className="px-3 py-2 border-b border-[#504A4A]">
          <input
            autoFocus
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') { setShowNewInput(false); setNewTitle(''); }
            }}
            onBlur={() => { if (!newTitle.trim()) setShowNewInput(false); }}
            placeholder="Notebook name..."
            className="w-full bg-[#2A2626] border border-[#504A4A] rounded-md px-2.5 py-1.5 text-xs text-[#EBFFEE] placeholder-[#6B6565] focus:outline-none focus:border-[#E8535E]/50"
          />
          <div className="flex items-center gap-1.5 mt-1.5">
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || isCreating}
              className="flex-1 text-[10px] py-1 rounded bg-[#9B1421] text-white font-medium disabled:opacity-40"
            >
              {isCreating ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={() => { setShowNewInput(false); setNewTitle(''); }}
              className="flex-1 text-[10px] py-1 rounded bg-white/5 text-[#A9A3A3] hover:text-[#EBFFEE]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notebook list */}
      <div className="flex-1 overflow-y-auto py-1.5 [scrollbar-width:thin] [scrollbar-color:#504A4A_transparent]">
        {/* All Notes — drop target for "remove from notebook" */}
        <div
          onDragOver={(e) => onDragOver(e, null)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, null)}
        >
          <button
            onClick={() => onSelect(null)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors border-l-2 ${
              dragOverNotebookId === 'none'
                ? 'bg-[#E8535E]/10 border-[#E8535E]'
                : activeNotebookId === null
                  ? 'bg-white/[0.08] text-[#EBFFEE] border-[#E8535E]'
                  : 'text-[#A9A3A3] hover:bg-white/[0.03] hover:text-[#EBFFEE] border-transparent'
            }`}
          >
            <FileText className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-sm truncate">All Notes</span>
            <span className="text-[10px] text-[#A9A3A3]/60">{totalNoteCount}</span>
          </button>
        </div>

        {/* Each notebook — drop target */}
        {notebooks.map((nb) => {
          const isActive = activeNotebookId === nb.id;
          const count = noteCountByNotebook[nb.id] ?? 0;
          const isDragOver = dragOverNotebookId === nb.id;

          return (
            <div
              key={nb.id}
              className="relative group"
              onDragOver={(e) => onDragOver(e, nb.id)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, nb.id)}
            >
              {renamingId === nb.id ? (
                <div className="px-3 py-1.5">
                  <input
                    autoFocus
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameSubmit(nb.id);
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    onBlur={() => handleRenameSubmit(nb.id)}
                    className="w-full bg-[#2A2626] border border-[#504A4A] rounded px-2 py-1 text-xs text-[#EBFFEE] focus:outline-none focus:border-[#E8535E]/50"
                  />
                </div>
              ) : (
                <button
                  onClick={() => onSelect(nb.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors border-l-2 ${
                    isDragOver
                      ? 'bg-[#E8535E]/10 border-[#E8535E]'
                      : isActive
                        ? 'bg-white/[0.08] text-[#EBFFEE] border-[#E8535E]'
                        : 'text-[#A9A3A3] hover:bg-white/[0.03] hover:text-[#EBFFEE] border-transparent'
                  }`}
                >
                  {isActive || isDragOver ? (
                    <FolderOpen className="w-4 h-4 shrink-0" />
                  ) : (
                    <FolderClosed className="w-4 h-4 shrink-0" />
                  )}
                  <span className="flex-1 text-sm truncate">{nb.title}</span>
                  <span className="text-[10px] text-[#A9A3A3]/60">{count}</span>
                </button>
              )}

              {/* Context menu trigger */}
              {renamingId !== nb.id && (
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === nb.id ? null : nb.id); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-[#A9A3A3] hover:text-[#EBFFEE] opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </button>
              )}

              {/* Context dropdown */}
              {menuOpen === nb.id && (
                <div ref={menuRef} className="absolute right-2 top-full z-20 bg-[#2A2626] border border-[#504A4A] rounded-lg shadow-xl py-1 min-w-[130px]">
                  <button
                    onClick={() => startRename(nb)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#EBFFEE] hover:bg-white/5 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                    Rename
                  </button>
                  <button
                    onClick={() => { onDelete(nb.id); setMenuOpen(null); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Note List Panel ────────────────────────────────────────────────────────

function NoteListPanel({
  notes,
  notebooks,
  activeNoteId,
  activeNotebookId,
  onSelect,
  onCreate,
  onMoveNote,
  onSelectNotebook,
  isLoading,
  isCreating,
  activeNotebookLabel,
  onDragStart,
}: {
  notes: Note[];
  notebooks: Notebook[];
  activeNoteId: string | null;
  activeNotebookId: string | null;
  onSelect: (note: Note) => void;
  onCreate: () => void;
  onMoveNote: (noteId: string, notebookId: string | null) => void;
  onSelectNotebook: (id: string | null) => void;
  isLoading: boolean;
  isCreating: boolean;
  activeNotebookLabel: string;
  onDragStart: (e: React.DragEvent, noteId: string) => void;
}) {
  const [moveMenuNoteId, setMoveMenuNoteId] = useState<string | null>(null);

  // Notebook name lookup for badge
  const notebookNames: Record<string, string> = {};
  notebooks.forEach((nb) => { notebookNames[nb.id] = nb.title; });

  return (
    <div className="w-full lg:w-72 lg:min-w-[288px] border-r border-[#504A4A] flex flex-col">
      {/* Header */}
      <div className="px-3 h-[52px] shrink-0 border-b border-[#504A4A] flex items-center justify-between">
        <div className="min-w-0 flex-1">
          {/* Mobile: notebook filter dropdown (sidebar is hidden below lg) */}
          <div className="lg:hidden">
            <select
              value={activeNotebookId ?? ''}
              onChange={(e) => onSelectNotebook(e.target.value || null)}
              className="bg-transparent text-sm font-semibold text-[#EBFFEE] focus:outline-none cursor-pointer w-full truncate appearance-none"
              style={{ backgroundImage: 'none' }}
            >
              <option value="" className="bg-[#221F1F] text-[#EBFFEE]">All Notes</option>
              {notebooks.map((nb) => (
                <option key={nb.id} value={nb.id} className="bg-[#221F1F] text-[#EBFFEE]">{nb.title}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#A9A3A3] leading-tight">{notes.length} {notes.length === 1 ? 'note' : 'notes'}</p>
          </div>
          {/* Desktop: static label (sidebar handles selection) */}
          <div className="hidden lg:block">
            <h3 className="text-sm font-semibold text-[#EBFFEE] truncate leading-tight">{activeNotebookLabel}</h3>
            <p className="text-[10px] text-[#A9A3A3] leading-tight">{notes.length} {notes.length === 1 ? 'note' : 'notes'}</p>
          </div>
        </div>
        <button
          onClick={onCreate}
          disabled={isCreating}
          className="w-7 h-7 rounded-lg bg-[#9B1421] hover:bg-[#7A0F1A] flex items-center justify-center text-white transition-colors shrink-0 disabled:opacity-50 ml-2"
          title="New note"
        >
          {isCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#504A4A_transparent]">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-4 h-4 animate-spin text-[#A9A3A3]" />
          </div>
        ) : notes.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <FileText className="w-8 h-8 text-[#504A4A] mx-auto mb-2" />
            <p className="text-xs text-[#A9A3A3]">No notes yet</p>
            <button
              onClick={onCreate}
              className="mt-2 text-xs text-[#E8535E] hover:text-[#EBFFEE] transition-colors font-medium"
            >
              Create your first note
            </button>
          </div>
        ) : (
          <div className="py-1">
            {notes.map((note) => (
              <div
                key={note.id}
                draggable
                onDragStart={(e) => onDragStart(e, note.id)}
                className={`relative group border-b border-[#504A4A]/20 transition-colors cursor-grab active:cursor-grabbing ${
                  activeNoteId === note.id
                    ? 'bg-white/[0.06]'
                    : 'hover:bg-white/[0.03]'
                }`}
              >
                <button
                  onClick={() => onSelect(note)}
                  className="w-full text-left px-3 py-3"
                >
                  <p className="text-sm font-medium text-[#EBFFEE] truncate pr-6">
                    {note.title || 'Untitled'}
                  </p>
                  <p className="text-[11px] text-[#A9A3A3] mt-1 line-clamp-2 leading-relaxed">
                    {note.body || 'No content'}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-[#A9A3A3]">{timeAgo(note.updated_at)}</span>
                    {/* Show notebook badge when in "All Notes" view */}
                    {!activeNotebookId && note.notebook_id && notebookNames[note.notebook_id] && (
                      <span className="text-[10px] text-[#A9A3A3]/70 bg-white/5 px-1.5 py-0.5 rounded truncate max-w-[120px]">
                        {notebookNames[note.notebook_id]}
                      </span>
                    )}
                  </div>
                </button>

                {/* Move-to button (visible on hover) */}
                <div className="absolute right-2 top-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setMoveMenuNoteId(moveMenuNoteId === note.id ? null : note.id); }}
                    className="p-1 rounded text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Move to notebook"
                  >
                    <FolderInput className="w-3.5 h-3.5" />
                  </button>
                  {moveMenuNoteId === note.id && (
                    <MoveToDropdown
                      notebooks={notebooks}
                      currentNotebookId={note.notebook_id}
                      onMove={(nbId) => onMoveNote(note.id, nbId)}
                      onClose={() => setMoveMenuNoteId(null)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Note Editor ────────────────────────────────────────────────────────────

function NoteEditor({
  note,
  notebooks,
  onSave,
  onDelete,
  onMove,
  onBack,
  isSaving,
  isDeleting,
}: {
  note: Note;
  notebooks: Notebook[];
  onSave: (title: string, body: string) => void;
  onDelete: () => void;
  onMove: (notebookId: string | null) => void;
  onBack: () => void;
  isSaving: boolean;
  isDeleting: boolean;
}) {
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const [dirty, setDirty] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  useEffect(() => {
    setTitle(note.title);
    setBody(note.body);
    setDirty(false);
  }, [note.id, note.title, note.body]);

  const handleSave = useCallback(() => {
    onSave(title, body);
    setDirty(false);
  }, [onSave, title, body]);

  // Ctrl/Cmd+S shortcut
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (dirty) handleSave();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [dirty, handleSave]);

  const notebookName = note.notebook_id
    ? notebooks.find((nb) => nb.id === note.notebook_id)?.title
    : null;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 h-[52px] shrink-0 border-b border-[#504A4A]">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="lg:hidden p-1.5 rounded text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="hidden lg:flex items-center gap-2">
            {/* Notebook badge */}
            <div className="relative">
              <button
                onClick={() => setShowMoveMenu(!showMoveMenu)}
                className="flex items-center gap-1.5 text-[11px] text-[#A9A3A3] hover:text-[#EBFFEE] bg-white/5 hover:bg-white/10 px-2 py-1 rounded-md transition-colors"
                title="Move to notebook"
              >
                <FolderClosed className="w-3 h-3" />
                {notebookName || 'No Notebook'}
              </button>
              {showMoveMenu && (
                <MoveToDropdown
                  notebooks={notebooks}
                  currentNotebookId={note.notebook_id}
                  onMove={(nbId) => { onMove(nbId); setShowMoveMenu(false); }}
                  onClose={() => setShowMoveMenu(false)}
                />
              )}
            </div>

            <span className="text-[10px] text-[#A9A3A3]">
              {dirty ? 'Unsaved changes' : `Saved ${timeAgo(note.updated_at)}`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="p-2 rounded-lg text-[#A9A3A3] hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Delete note"
          >
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || isSaving}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="px-6 pt-5">
        <input
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
          placeholder="Note title..."
          className="w-full bg-transparent text-2xl font-bold text-[#EBFFEE] placeholder-[#6B6565] focus:outline-none"
          autoFocus={!note.title}
        />
      </div>

      {/* Body */}
      <div className="flex-1 px-6 pt-4 pb-6">
        <textarea
          value={body}
          onChange={(e) => { setBody(e.target.value); setDirty(true); }}
          placeholder="Start writing..."
          className="w-full h-full bg-transparent text-[15px] text-[#EBFFEE]/90 placeholder-[#6B6565] resize-none focus:outline-none leading-relaxed"
        />
      </div>
    </div>
  );
}

// ─── Empty Editor State ─────────────────────────────────────────────────────

function EmptyEditor({ onCreate, isCreating }: { onCreate: () => void; isCreating: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6">
      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
        <BookOpen className="w-8 h-8 text-[#504A4A]" />
      </div>
      <h3 className="text-lg font-semibold text-[#EBFFEE]">Select or create a note</h3>
      <p className="text-sm text-[#A9A3A3] mt-1.5 max-w-[280px]">
        Pick a note from the list or create a new one to start journaling.
      </p>
      <button
        onClick={onCreate}
        disabled={isCreating}
        className="mt-4 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-medium transition-colors disabled:opacity-50"
      >
        {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        New Note
      </button>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function StudentJournal() {
  const [activeNotebookId, setActiveNotebookId] = useState<string | null>(null);
  const [activeNote, setActiveNote] = useState<Note | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'editor'>('list');
  const [dragOverNotebookId, setDragOverNotebookId] = useState<string | null>(null);
  const dragNoteIdRef = useRef<string | null>(null);

  const { notebooks } = useNotebooks();
  const { notes: allNotes, isLoading: allNotesLoading } = useNotes(null);
  const { notes: filteredNotes, isLoading: filteredLoading } = useNotes(activeNotebookId);
  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();
  const moveNote = useMoveNote();
  const createNotebook = useCreateNotebook();
  const renameNotebook = useRenameNotebook();
  const deleteNotebook = useDeleteNotebook();

  const displayNotes = activeNotebookId ? filteredNotes : allNotes;
  const displayLoading = activeNotebookId ? filteredLoading : allNotesLoading;

  const noteCountByNotebook: Record<string, number> = {};
  allNotes.forEach((n) => {
    if (n.notebook_id) {
      noteCountByNotebook[n.notebook_id] = (noteCountByNotebook[n.notebook_id] || 0) + 1;
    }
  });

  const activeNotebookLabel = activeNotebookId
    ? notebooks.find((nb) => nb.id === activeNotebookId)?.title ?? 'Notebook'
    : 'All Notes';

  // Keep active note synced
  useEffect(() => {
    if (activeNote) {
      const updated = displayNotes.find((n) => n.id === activeNote.id);
      if (updated) setActiveNote(updated);
    }
  }, [displayNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ─────────────────────────────────────────────────────

  const handleCreateNote = useCallback(async () => {
    const result = await createNote.mutateAsync({
      title: '',
      body: '',
      notebookId: activeNotebookId,
    });
    setActiveNote({
      id: result.id,
      title: '',
      body: '',
      notebook_id: activeNotebookId,
      user_id: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
    setMobileView('editor');
  }, [activeNotebookId, createNote]);

  const handleSaveNote = useCallback(async (title: string, body: string) => {
    if (!activeNote) return;
    await updateNote.mutateAsync({ noteId: activeNote.id, title, body });
  }, [activeNote, updateNote]);

  const handleDeleteNote = useCallback(async () => {
    if (!activeNote) return;
    await deleteNote.mutateAsync(activeNote.id);
    setActiveNote(null);
    setMobileView('list');
  }, [activeNote, deleteNote]);

  const handleMoveNote = useCallback(async (noteId: string, notebookId: string | null) => {
    await moveNote.mutateAsync({ noteId, notebookId });
    // Update active note if it was moved
    if (activeNote?.id === noteId) {
      setActiveNote((prev) => prev ? { ...prev, notebook_id: notebookId } : null);
    }
  }, [moveNote, activeNote]);

  const handleMoveActiveNote = useCallback(async (notebookId: string | null) => {
    if (!activeNote) return;
    await handleMoveNote(activeNote.id, notebookId);
  }, [activeNote, handleMoveNote]);

  const handleCreateNotebook = useCallback(async (title: string) => {
    await createNotebook.mutateAsync(title);
  }, [createNotebook]);

  const handleRenameNotebook = useCallback(async (id: string, title: string) => {
    await renameNotebook.mutateAsync({ notebookId: id, title });
  }, [renameNotebook]);

  const handleDeleteNotebook = useCallback(async (id: string) => {
    await deleteNotebook.mutateAsync(id);
    if (activeNotebookId === id) setActiveNotebookId(null);
  }, [deleteNotebook, activeNotebookId]);

  const handleSelectNote = useCallback((note: Note) => {
    setActiveNote(note);
    setMobileView('editor');
  }, []);

  // ─── Drag & Drop ─────────────────────────────────────────────────

  const handleDragStart = useCallback((e: React.DragEvent, noteId: string) => {
    dragNoteIdRef.current = noteId;
    e.dataTransfer.effectAllowed = 'move';
    // Add a subtle drag image effect
    const el = e.currentTarget as HTMLElement;
    el.style.opacity = '0.5';
    setTimeout(() => { el.style.opacity = '1'; }, 0);
  }, []);

  const handleDragOverNotebook = useCallback((e: React.DragEvent, notebookId: string | null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverNotebookId(notebookId === null ? 'none' : notebookId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverNotebookId(null);
  }, []);

  const handleDropOnNotebook = useCallback((e: React.DragEvent, notebookId: string | null) => {
    e.preventDefault();
    setDragOverNotebookId(null);
    const noteId = dragNoteIdRef.current;
    if (noteId) {
      handleMoveNote(noteId, notebookId);
      dragNoteIdRef.current = null;
    }
  }, [handleMoveNote]);

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div className="py-6 h-[calc(100vh-4rem)]">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#EBFFEE]">Training Journal</h1>
            <p className="text-sm text-[#A9A3A3] mt-1">Track techniques, progress, and session notes.</p>
          </div>
        </div>

        {/* 3-panel layout */}
        <div className="flex-1 min-h-0 bg-[#221F1F] border border-[#504A4A] rounded-xl overflow-hidden flex">
          {/* Panel 1: Notebook Sidebar */}
          <div className="hidden lg:flex">
            <NotebookSidebar
              notebooks={notebooks}
              activeNotebookId={activeNotebookId}
              onSelect={setActiveNotebookId}
              onCreate={handleCreateNotebook}
              onRename={handleRenameNotebook}
              onDelete={handleDeleteNotebook}
              isCreating={createNotebook.isPending}
              noteCountByNotebook={noteCountByNotebook}
              totalNoteCount={allNotes.length}
              dragOverNotebookId={dragOverNotebookId}
              onDragOver={handleDragOverNotebook}
              onDragLeave={handleDragLeave}
              onDrop={handleDropOnNotebook}
            />
          </div>

          {/* Panel 2: Note List — full width on mobile, fixed width on desktop */}
          <div className={`${mobileView === 'list' ? 'flex flex-1 lg:flex-none' : 'hidden'} lg:flex`}>
            <NoteListPanel
              notes={displayNotes}
              notebooks={notebooks}
              activeNoteId={activeNote?.id ?? null}
              activeNotebookId={activeNotebookId}
              onSelect={handleSelectNote}
              onCreate={handleCreateNote}
              onMoveNote={handleMoveNote}
              onSelectNotebook={setActiveNotebookId}
              isLoading={displayLoading}
              isCreating={createNote.isPending}
              activeNotebookLabel={activeNotebookLabel}
              onDragStart={handleDragStart}
            />
          </div>

          {/* Panel 3: Editor — full width on mobile, flex on desktop */}
          <div className={`flex-1 min-w-0 ${mobileView === 'editor' ? 'flex flex-col' : 'hidden lg:flex lg:flex-col'}`}>
            {activeNote ? (
              <NoteEditor
                note={activeNote}
                notebooks={notebooks}
                onSave={handleSaveNote}
                onDelete={handleDeleteNote}
                onMove={handleMoveActiveNote}
                onBack={() => setMobileView('list')}
                isSaving={updateNote.isPending}
                isDeleting={deleteNote.isPending}
              />
            ) : (
              <EmptyEditor onCreate={handleCreateNote} isCreating={createNote.isPending} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
